const SENSITIVE_KEY_PATTERN =
  /(pass(word)?|secret|token|api[-_]?key|authorization|cookie|set-cookie|jwt|refresh[_-]?token|access[_-]?token|client[_-]?secret|private[_-]?key|email|phone|ssn)/i;
const NETWORK_IDENTIFIER_KEY_PATTERN =
  /(^|[-_.])(ip|forwarded)([-_.]|$)|proxied[-_]for|remote[-_]?addr|client[-_.]?address|peer[-_.]?addr(ess)?/i;

const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const BEARER_PATTERN = /(Bearer\s+)[^\s,;]+/gi;
const QUERY_SECRET_PATTERN =
  /([?&](token|api_key|apikey|auth|authorization|access_token|refresh_token|client_secret|fb_exchange_token|input_token|code))=([^&\s]+)/gi;

const REDACTED = '[redacted]';
const REDACTED_EMAIL = '[redacted-email]';

export const isSensitiveLogKey = (key: string): boolean =>
  SENSITIVE_KEY_PATTERN.test(key) || NETWORK_IDENTIFIER_KEY_PATTERN.test(key);
const isSensitiveKey = isSensitiveLogKey;

/** Removes bearer tokens and credential query parameters from free text. */
export const redactCredentials = (value: string): string =>
  value.replace(BEARER_PATTERN, `$1${REDACTED}`).replace(QUERY_SECRET_PATTERN, `$1=${REDACTED}`);

const sanitizeString = (value: string): string =>
  redactCredentials(value.replace(EMAIL_PATTERN, REDACTED_EMAIL));

export function sanitizeForLog(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value;
  if (depth > 5) return '[truncated]';

  if (typeof value === 'string') return sanitizeString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Date) return value.toISOString();
  if (value instanceof Error) return sanitizeErrorForLog(value);
  if (Array.isArray(value)) return value.map((item) => sanitizeForLog(item, depth + 1));
  if (typeof value === 'object')
    return sanitizeObjectForLog(value as Record<string, unknown>, depth);
  return String(value);
}

// One level of `cause` keeps the underlying reason when an error is wrapped. Supabase
// returns plain `{ message, code }` objects rather than Errors, so those count too.
const sanitizeCause = (cause: unknown) => {
  if (cause instanceof Error) return { name: cause.name, message: sanitizeString(cause.message) };
  if (!cause || typeof cause !== 'object') return undefined;
  const { message, code } = cause as { message?: unknown; code?: unknown };
  if (typeof message !== 'string') return undefined;
  return {
    ...(typeof code === 'string' ? { code } : {}),
    message: sanitizeString(message),
  };
};

const sanitizeErrorForLog = (value: Error) => {
  const cause = sanitizeCause(value.cause);
  return {
    name: value.name,
    message: sanitizeString(value.message),
    stack: value.stack ? sanitizeString(value.stack) : undefined,
    ...(cause ? { cause } : {}),
  };
};

const sanitizeObjectForLog = (record: Record<string, unknown>, depth: number) => {
  const out: Record<string, unknown> = {};
  for (const [key, inner] of Object.entries(record)) {
    if (isSensitiveKey(key)) {
      out[key] = REDACTED;
    } else {
      out[key] = sanitizeForLog(inner, depth + 1);
    }
  }
  return out;
};

export function sanitizeHeadersForLog(
  headers: Record<string, string | string[] | undefined>,
): Record<string, string | string[] | undefined> {
  const out: Record<string, string | string[] | undefined> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (isSensitiveKey(key)) {
      out[key] = REDACTED;
      continue;
    }

    if (Array.isArray(value)) {
      out[key] = value.map((item) => sanitizeString(item));
      continue;
    }

    out[key] = typeof value === 'string' ? sanitizeString(value) : value;
  }
  return out;
}
